'use strict';

/**
 * EvoWalk sensor channels on legacy (penguin) trials. Names keep the lab's
 * firmware names (x_accel, percent, stim, …) and their raw units, which changed
 * between device revisions, so none of the live sensor labels, units or
 * thresholds (accel_x, gaitVals_percent, isWalk) apply. Legacy `percent` is −1…1.
 *
 * One device: bare channel names. Several: `<channel>_<device>`, where device is
 * `left` / `right`, a placement (LL…RU), or the cleaned device name when two
 * devices share a side (e.g. `x_gyro_Evowalk_Right_foot`).
 */

export const LEGACY_SENSOR_CHANNELS = [
  'x_accel', 'y_accel', 'z_accel', 'x_gyro', 'y_gyro', 'z_gyro',
  'roll', 'pitch', 'heading', 'ycos', 'ysin', 'percent', 'mag',
  'cadence', 'step_count', 'stance', 'swing',
  'switch_state', 'switch_normal', 'stimonoff_state', 'stim_good_time', 'stim',
  'a0', 'a1', 'a2', 'a3', 'g0', 'g1', 'g2', 'g3', 'o0', 'o1', 'o2', 'o3', 'test',
];

/** Device id used when the trial has a single, unsuffixed device. */
export const SINGLE_DEVICE_ID = 'device';

const KNOWN_LONGEST_FIRST = [...LEGACY_SENSOR_CHANNELS].sort((a, b) => b.length - a.length);
const SIDE_ORDER = ['left', 'right'];

/** Accelerometer and gyroscope axes are charted by default. */
export function isDefaultLegacyChannel(channel) {
  return /^[xyz]_(accel|gyro)$/.test(String(channel || ''));
}

/** On/off and phase channels: drawn as steps, not interpolated lines. */
export function isLegacyStateChannel(channel) {
  const name = String(channel || '');
  return /^(switch_|stim)/.test(name) || name === 'stance' || name === 'swing';
}

function matchKnownChannel(name) {
  for (const channel of KNOWN_LONGEST_FIRST) {
    if (name === channel) return { channel, device: '' };
    if (name.startsWith(`${channel}_`)) return { channel, device: name.slice(channel.length + 1) };
  }
  return null;
}

/** `[{ name, channel, deviceId }]` in input order. */
export function parseLegacySensorNames(names) {
  const list = Array.isArray(names) ? names.filter((n) => typeof n === 'string' && n) : [];
  const known = list.map(matchKnownChannel);
  const devices = [...new Set(known.filter((m) => m && m.device).map((m) => m.device))]
    .sort((a, b) => b.length - a.length);
  return list.map((name, i) => {
    let match = known[i];
    if (!match) {
      const device = devices.find((d) => name.endsWith(`_${d}`))
        || (/_(left|right)$/i.exec(name) || [])[1];
      match = device
        ? { channel: name.slice(0, -(device.length + 1)), device }
        : { channel: name, device: '' };
    }
    return { name, channel: match.channel, deviceId: match.device || SINGLE_DEVICE_ID };
  });
}

export function legacySensorDeviceLabel(deviceId) {
  if (!deviceId || deviceId === SINGLE_DEVICE_ID) return 'EvoWalk';
  const lower = deviceId.toLowerCase();
  if (SIDE_ORDER.includes(lower)) return `${lower[0].toUpperCase()}${lower.slice(1)} EvoWalk`;
  if (/^(LL|LU|RL|RU)$/.test(deviceId)) return `EvoWalk ${deviceId}`;
  return deviceId.replace(/_+/g, ' ');
}

function channelRank(channel) {
  const index = LEGACY_SENSOR_CHANNELS.indexOf(channel);
  return index === -1 ? LEGACY_SENSOR_CHANNELS.length : index;
}

function deviceRank(deviceId) {
  const index = SIDE_ORDER.indexOf(deviceId.toLowerCase());
  return index === -1 ? SIDE_ORDER.length : index;
}

/**
 * Channels grouped per device: left, right, then other devices by name. Inside a
 * device, channels follow LEGACY_SENSOR_CHANNELS, then unknown ones by name.
 */
export function groupLegacySensors(names) {
  const groups = new Map();
  parseLegacySensorNames(names).forEach((entry) => {
    if (!groups.has(entry.deviceId)) groups.set(entry.deviceId, []);
    groups.get(entry.deviceId).push({
      ...entry,
      stepped: isLegacyStateChannel(entry.channel),
    });
  });
  return [...groups.entries()]
    .map(([deviceId, channels]) => ({
      deviceId,
      label: legacySensorDeviceLabel(deviceId),
      channels: channels.sort(
        (a, b) => channelRank(a.channel) - channelRank(b.channel) || a.channel.localeCompare(b.channel)
      ),
    }))
    .sort((a, b) => deviceRank(a.deviceId) - deviceRank(b.deviceId) || a.deviceId.localeCompare(b.deviceId));
}

export function defaultLegacySensorNames(groups) {
  return (Array.isArray(groups) ? groups : []).flatMap((group) =>
    group.channels.filter((entry) => isDefaultLegacyChannel(entry.channel)).map((entry) => entry.name)
  );
}

/** `name -> { channel, deviceId, stepped, deviceLabel }` for every grouped channel. */
export function indexLegacySensors(groups) {
  const index = new Map();
  (Array.isArray(groups) ? groups : []).forEach((group) => {
    group.channels.forEach((entry) => {
      index.set(entry.name, { ...entry, deviceLabel: group.label });
    });
  });
  return index;
}
